import React, { useState, useEffect, useRef } from 'react';
import {
  Buildings, TreeStructure, Ticket, ArrowsLeftRight, ArrowUUpLeft, ShareNetwork, Info,
  ArrowClockwise, Check, X, ArrowRight, Tray,
} from '@phosphor-icons/react';
import {
  getCoBranchNames, getBranchNames, getGroupCode, getTickets,
  getCentralOfficeChitDetails, updateAdvanceInterestPaymentBank, saveData,
} from '../api/connectionRegistryApi';
import { useLoginName } from '../utils/auditUser';
import SearchableSelect from './SearchableSelect';
import Toast from './Toast';
import { useToast } from '../utils/useToast';
import { NO_DATA_MESSAGE, NO_DATA_SUMMARY, isEmptyResult } from '../utils/noData';

const ACTION_TYPE_MAP = {
  SELF_TO_ADJUSTMENT: 1,
  ADJUSTMENT_TO_SELF: 2,
  GROUPCODE_CHANGE: 3,
};

const ACTION_LABELS = {
  SELF_TO_ADJUSTMENT: 'Self to Adjustment',
  ADJUSTMENT_TO_SELF: 'Adjustment to Self (Reverse)',
  GROUPCODE_CHANGE: 'Groupcode Change',
};

// One change type per action, not one for the screen. These are three different
// operations — a transfer out, its reverse, and a group re-point — and writing
// them all as CENTRAL_OFFICE_CHITS_ADJUSTMENT left Reports Of Updates unable to
// tell them apart or filter to one of them; the action survived only as a field
// buried inside the JSON. Agent Code Change already works this way, a type per
// mode. No history is split by this: no row was ever written under the old
// umbrella type.
const ACTION_CHANGE_TYPES = {
  SELF_TO_ADJUSTMENT: 'CENTRAL_OFFICE_CHITS_SELF_TO_ADJUSTMENT',
  ADJUSTMENT_TO_SELF: 'CENTRAL_OFFICE_CHITS_ADJUSTMENT_TO_SELF',
  GROUPCODE_CHANGE: 'CENTRAL_OFFICE_CHITS_GROUPCODE_CHANGE',
};

function todayIso() {
  return new Date().toISOString().substring(0, 10);
}

const ACTION_BOXES = [
  { key: 'SELF_TO_ADJUSTMENT', icon: ArrowsLeftRight, label: 'Self to Adjustment' },
  { key: 'ADJUSTMENT_TO_SELF', icon: ArrowUUpLeft, label: 'Adjustment to Self ( Reverse )' },
  { key: 'GROUPCODE_CHANGE', icon: ShareNetwork, label: 'Groupcode Change' },
];

function CentralOfficeChitsForm() {
  const loginName = useLoginName();
  const [formKey, setFormKey] = useState(0);

  // ── Source ──
  const [sourceCaoList, setSourceCaoList] = useState([]);
  const [sourceGroupList, setSourceGroupList] = useState([]);
  const [sourceTicketList, setSourceTicketList] = useState([]);

  const [sourceCao, setSourceCao] = useState('');
  const [sourceBranchSchema, setSourceBranchSchema] = useState(null);
  const [sourceBranchCode, setSourceBranchCode] = useState(null);
  const [sourceBranchId, setSourceBranchId] = useState(null);

  const [sourceGroup, setSourceGroup] = useState('');
  const [sourceGroupCode, setSourceGroupCode] = useState(null);
  const [sourceChitGroupId, setSourceChitGroupId] = useState(null);

  const [sourceTicket, setSourceTicket] = useState('');
  const [sourceTicketNo, setSourceTicketNo] = useState(null);
  const [sourceSubscriberName, setSourceSubscriberName] = useState(null);

  // ── Target ──
  const [targetCaoList, setTargetCaoList] = useState([]);
  const [targetGroupList, setTargetGroupList] = useState([]);
  const [targetTicketList, setTargetTicketList] = useState([]);

  const [targetCao, setTargetCao] = useState('');
  const [targetBranchCode, setTargetBranchCode] = useState(null);
  const [targetBranchId, setTargetBranchId] = useState(null);

  const [targetGroup, setTargetGroup] = useState('');
  const [targetGroupCode, setTargetGroupCode] = useState(null);
  const [targetChitGroupId, setTargetChitGroupId] = useState(null);

  const [targetTicket, setTargetTicket] = useState('');
  const [targetTicketNo, setTargetTicketNo] = useState(null);

  const [selectedAction, setSelectedAction] = useState(null);
  const [chitRecords, setChitRecords] = useState([]);
  const [reason, setReason] = useState('');
  const adjustmentDateRef = useRef(todayIso());

  const [sourceTouched, setSourceTouched] = useState(false);
  const [targetTouched, setTargetTouched] = useState(false);
  const [reasonTouched, setReasonTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const { toast, setToast, showToast } = useToast();

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingActionType, setPendingActionType] = useState(null);
  const [pendingActionLabel, setPendingActionLabel] = useState('');
  // Snapshotted with the rest of the pending state so the type that is written
  // is the one the confirm modal described, even if the action is re-picked.
  const [pendingChangeType, setPendingChangeType] = useState('');
  const [pendingSourceSummary, setPendingSourceSummary] = useState('');
  const [pendingTargetSummary, setPendingTargetSummary] = useState('');
  const [pendingRecordCount, setPendingRecordCount] = useState(0);
  const [pendingReason, setPendingReason] = useState('');

  useEffect(() => {
    getCoBranchNames().then((res) => setSourceCaoList(res || [])).catch(() => setSourceCaoList([]));
    getBranchNames().then((res) => setTargetCaoList(res || [])).catch(() => setTargetCaoList([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formKey]);

  // ── Source reset chain ──
  const resetSourceFromTicket = () => {
    setSourceTicketNo(null);
    setSourceSubscriberName(null);
    setChitRecords([]);
    setSelectedAction(null);
  };

  const resetSourceFromGroup = () => {
    setSourceGroupCode(null);
    setSourceChitGroupId(null);
    setSourceTicket('');
    setSourceTicketList([]);
    resetSourceFromTicket();
  };

  const resetSourceFromBranch = () => {
    setSourceBranchSchema(null);
    setSourceBranchCode(null);
    setSourceBranchId(null);
    setSourceGroup('');
    setSourceGroupList([]);
    resetSourceFromGroup();
    setSelectedAction(null);
  };

  // ── Target reset chain ──
  const resetTargetFromGroup = () => {
    setTargetGroupCode(null);
    setTargetChitGroupId(null);
    setTargetTicket('');
    setTargetTicketNo(null);
    setTargetTicketList([]);
  };

  const resetTargetFromBranch = () => {
    setTargetBranchCode(null);
    setTargetBranchId(null);
    setTargetGroup('');
    setTargetGroupList([]);
    resetTargetFromGroup();
  };

  // ── Source handlers ──
  const handleSourceCaoChange = async (branchName) => {
    setSourceCao(branchName);
    resetSourceFromBranch();
    if (!branchName) return;

    const selected = sourceCaoList.find((b) => b.branch_name === branchName);
    if (!selected) return;

    setSourceBranchSchema(selected.branch_code);
    setSourceBranchCode(selected.branch_code);
    setSourceBranchId(selected.tbl_mst_branch_configuration_id);

    try {
      const res = await getGroupCode(selected.branch_code);
      setSourceGroupList(res || []);
      if (isEmptyResult(res)) showToast('warn', NO_DATA_SUMMARY, NO_DATA_MESSAGE);
    } catch {
      setSourceGroupList([]);
    }
  };

  const handleSourceGroupChange = async (groupCodeValue) => {
    setSourceGroup(groupCodeValue);
    resetSourceFromGroup();
    if (!groupCodeValue) return;

    const selected = sourceGroupList.find((g) => String(g.group_Code) === String(groupCodeValue));
    if (!selected) return;

    setSourceGroupCode(selected.group_Code);
    setSourceChitGroupId(Number(selected.chitgroup_id));

    try {
      const res = await getTickets(sourceBranchCode, selected.group_Code);
      setSourceTicketList(res || []);
      if (isEmptyResult(res)) showToast('warn', NO_DATA_SUMMARY, NO_DATA_MESSAGE);
    } catch {
      setSourceTicketList([]);
    }
  };

  const handleSourceTicketChange = async (ticketNoValue) => {
    setSourceTicket(ticketNoValue);
    resetSourceFromTicket();
    if (!ticketNoValue) return;

    const selected = sourceTicketList.find((t) => String(t.ticketno) === String(ticketNoValue));
    if (!selected) return;

    setSourceTicketNo(selected.ticketno);

    try {
      const res = await getCentralOfficeChitDetails(
        sourceBranchSchema, sourceBranchCode, sourceGroupCode, selected.ticketno,
      );
      const records = res || [];
      setChitRecords(records);
      if (isEmptyResult(records)) showToast('warn', NO_DATA_SUMMARY, NO_DATA_MESSAGE);
      setSourceSubscriberName(
        records.length > 0 ? records.map((r) => r.subscriber_name).filter(Boolean).join(', ') : null,
      );
    } catch (err) {
      console.error('Failed to fetch central office chit details:', err);
      setSourceSubscriberName(null);
      showToast('error', 'Load Failed', 'Failed to fetch chit details.');
    }
  };

  // ── Target handlers ──
  const handleTargetCaoChange = async (branchName) => {
    setTargetCao(branchName);
    resetTargetFromBranch();
    if (!branchName) return;

    const selected = targetCaoList.find((b) => b.branch_name === branchName);
    if (!selected) return;

    setTargetBranchCode(selected.branch_code);
    setTargetBranchId(selected.tbl_mst_branch_configuration_id);

    try {
      const res = await getGroupCode(selected.branch_code);
      setTargetGroupList(res || []);
      if (isEmptyResult(res)) showToast('warn', NO_DATA_SUMMARY, NO_DATA_MESSAGE);
    } catch {
      setTargetGroupList([]);
    }
  };

  const handleTargetGroupChange = async (groupCodeValue) => {
    setTargetGroup(groupCodeValue);
    resetTargetFromGroup();
    if (!groupCodeValue) return;

    const selected = targetGroupList.find((g) => String(g.group_Code) === String(groupCodeValue));
    if (!selected) return;

    setTargetGroupCode(selected.group_Code);
    setTargetChitGroupId(Number(selected.chitgroup_id));

    try {
      const res = await getTickets(targetBranchCode, selected.group_Code);
      setTargetTicketList(res || []);
      if (isEmptyResult(res)) showToast('warn', NO_DATA_SUMMARY, NO_DATA_MESSAGE);
    } catch {
      setTargetTicketList([]);
    }
  };

  const handleTargetTicketChange = (ticketNoValue) => {
    setTargetTicket(ticketNoValue);
    if (!ticketNoValue) {
      setTargetTicketNo(null);
      return;
    }
    const selected = targetTicketList.find((t) => String(t.ticketno) === String(ticketNoValue));
    if (selected) setTargetTicketNo(selected.ticketno);
  };

  // Only resets the target sub-group — never touches source ticket/chitRecords,
  // otherwise the buttons (which only render once a source ticket is picked)
  // would disappear right after being clicked.
  const selectAction = (key) => {
    if (selectedAction === key) return;
    setSelectedAction(key);
    setTargetCao('');
    resetTargetFromBranch();
  };

  const needsTarget = selectedAction === 'SELF_TO_ADJUSTMENT' || selectedAction === 'GROUPCODE_CHANGE';

  const sourceCaoInvalid = sourceTouched && !sourceCao;
  const sourceGroupInvalid = sourceTouched && !sourceGroup;
  const sourceTicketInvalid = sourceTouched && !sourceTicket;

  const targetCaoInvalid = targetTouched && !targetCao;
  const targetGroupInvalid = targetTouched && !targetGroup;
  const targetTicketInvalid = targetTouched && !targetTicket;

  const reasonInvalid = reasonTouched && (!reason || reason.length > 500);

  const confirmAdjust = () => {
    if (!selectedAction) {
      showToast('warn', 'Action Required', 'Please select an action (Self to Adjustment, Adjustment to Self, or Groupcode Change) first.');
      return;
    }

    setSourceTouched(true);
    setReasonTouched(true);

    if (!sourceCao || !sourceGroup || !sourceTicket) {
      showToast('warn', 'Incomplete Form', 'Please complete all required source fields.');
      return;
    }

    if (chitRecords.length === 0) {
      showToast('warn', 'No Data Found', 'There are no chit records available to adjust for this ticket.');
      return;
    }

    const actionType = ACTION_TYPE_MAP[selectedAction];

    if (actionType === 1 || actionType === 3) {
      setTargetTouched(true);
      if (!targetCao || !targetGroup || !targetTicket) {
        showToast('warn', 'Incomplete Form', 'Please complete all required target fields.');
        return;
      }
      if (
        sourceBranchId === targetBranchId &&
        sourceChitGroupId === targetChitGroupId &&
        sourceTicketNo === targetTicketNo
      ) {
        showToast('warn', 'Invalid Target', 'Target branch/group/ticket must be different from the source.');
        return;
      }
    }

    if (!reason || reason.length > 500) {
      showToast('warn', 'Reason Required', 'Please enter a reason for this adjustment.');
      return;
    }

    setPendingActionType(actionType);
    setPendingActionLabel(ACTION_LABELS[selectedAction]);
    setPendingChangeType(ACTION_CHANGE_TYPES[selectedAction]);
    setPendingSourceSummary(`${sourceBranchCode} / ${sourceGroupCode} / ${sourceTicketNo}`);
    setPendingTargetSummary(
      (actionType === 1 || actionType === 3) ? `${targetBranchCode} / ${targetGroupCode} / ${targetTicketNo}` : '',
    );
    setPendingRecordCount(chitRecords.length);
    setPendingReason(reason);

    setShowConfirmModal(true);
  };

  const cancelConfirm = () => {
    if (saving) return;
    setShowConfirmModal(false);
  };

  const doSave = async () => {
    const actionType = pendingActionType;

    setSaving(true);

    const adjustmentDateIso = new Date(adjustmentDateRef.current).toISOString();

    const calls = chitRecords.map((row) => {
      const payload = {
        contactId: Number(row.contact_id),
        chitGroupId: Number(sourceChitGroupId),
        ticketNo: Number(sourceTicketNo),
        adjustmentDate: adjustmentDateIso,
        branchId: Number(sourceBranchId),
        actionType,
      };

      if (actionType === 1 || actionType === 3) {
        payload.adjustedBranchId = Number(targetBranchId);
        payload.adjustedChitGroupId = Number(targetChitGroupId);
        payload.adjustedTicketNo = Number(targetTicketNo);
      }

      // Action 2 (Adjustment to Self) — backend re-inserts the primary bank row,
      // so it needs bank_id / bank_branch_name / bank_account_number / bank_ifsc_code,
      // sourced from the chit record already fetched for this contact.
      if (actionType === 2) {
        payload.bankId = Number(row.bank_id);
        payload.bankBranchName = row.bank_branch_name;
        payload.bankAccountNumber = row.bank_account_number;
        payload.bankIFSCCode = row.bank_ifsc_code;
      }

      return updateAdvanceInterestPaymentBank(sourceBranchSchema, payload)
        .then((result) => (
          result === 'Success'
            ? { status: true }
            : { status: false, message: result || 'Request failed' }
        ))
        .catch((err) => ({
          status: false,
          message: err?.error?.Message || err?.message || 'Request failed',
        }));
    });

    const results = await Promise.all(calls);

    const failed = [];
    const succeededIds = [];

    results.forEach((res, i) => {
      const contactId = chitRecords[i].contact_id;
      if (res?.status === true) {
        succeededIds.push(contactId);
      } else {
        failed.push(`Contact ${contactId}: ${res?.message || 'Unknown error'}`);
      }
    });

    // Only the records that actually went through are audited — a partial run
    // must not log adjustments that failed. One entry per adjusted contact,
    // matching the per-row shape the other Supporting Tools screens write.
    let auditNote = '';
    if (succeededIds.length > 0) {
      const isTransfer = actionType === 1 || actionType === 3;
      const source = {
        branchCode: sourceBranchCode, groupCode: sourceGroupCode, ticketNo: sourceTicketNo,
      };
      const target = isTransfer
        ? { branchCode: targetBranchCode, groupCode: targetGroupCode, ticketNo: targetTicketNo }
        : source;

      const auditEntries = chitRecords
        .filter((r) => succeededIds.includes(r.contact_id))
        .map((r) => ({
          schemaName: sourceBranchSchema || null,
          loginName,
          loginTime: new Date().toISOString(),
          changeType: pendingChangeType,
          oldData: {
            action: pendingActionLabel,
            actionType,
            contactId: r.contact_id,
            subscriberName: r.subscriber_name,
            ...source,
          },
          newData: {
            action: pendingActionLabel,
            actionType,
            contactId: r.contact_id,
            subscriberName: r.subscriber_name,
            adjustmentDate: adjustmentDateIso,
            ...target,
          },
          reason: pendingReason,
        }));

      try {
        await saveData(auditEntries);
      } catch (auditErr) {
        console.error('Audit save failed:', auditErr);
        auditNote = ' (audit log failed to save — this change will not appear in Reports of Updates)';
      }
    }

    setSaving(false);
    setShowConfirmModal(false);

    let remainingRecords = chitRecords;
    if (succeededIds.length > 0) {
      remainingRecords = chitRecords.filter((r) => !succeededIds.includes(r.contact_id));
      setChitRecords(remainingRecords);
    }

    if (failed.length === 0) {
      showToast(auditNote ? 'warn' : 'success', auditNote ? 'Partial Success' : 'Success', `All record(s) adjusted successfully.${auditNote}`, true);
      handleClear();
    } else if (succeededIds.length === 0) {
      showToast('error', 'Adjustment Failed', `${failed.length} record(s) failed: ${failed.join('; ')}`, true);
    } else {
      showToast('warn', 'Partial Success', `${succeededIds.length} record(s) adjusted successfully. ${failed.length} failed: ${failed.join('; ')}${auditNote}`, true);
    }
  };

  const handleClear = () => {
    setFormKey((k) => k + 1);
    setSourceCao('');
    resetSourceFromBranch();
    setTargetCao('');
    resetTargetFromBranch();
    setChitRecords([]);
    setSelectedAction(null);
    setReason('');
    adjustmentDateRef.current = todayIso();
    setSourceTouched(false);
    setTargetTouched(false);
    setReasonTouched(false);
  };

  const showOldRecordsDivider = chitRecords.length > 0 || !!sourceTicket;

  return (
    <div className="nc-page" style={{ position: 'relative' }}>
      <Toast toast={toast} onClose={() => setToast(null)} testId="central-office-chits-toast" />

      <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '20px', position: 'relative' }} data-testid="central-office-chits-form">
        {saving && (
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: '#002FA7', opacity: 0.6, borderRadius: '8px 8px 0 0' }} />
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
          <div style={{ width: '34px', height: '34px', borderRadius: '8px', background: '#EFF4FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ArrowsLeftRight size={18} color="#002FA7" />
          </div>
          <div>
            <div style={{ fontSize: '11px', fontWeight: '600', color: '#002FA7', textTransform: 'uppercase' }}>Chit Advance Interest</div>
            <div style={{ fontSize: '16px', fontWeight: '700', color: '#111827' }}>Central Office Chits — Adjustment</div>
          </div>
        </div>

        {/* SECTION A: Source Ticket */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>A</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Source Ticket</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Choose the branch, group code and ticket to adjust from</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              <Buildings size={13} /> Central Office <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`source-cao-${formKey}`}
              options={sourceCaoList}
              getLabel={(o) => o.branch_name}
              getValue={(o) => o.branch_name}
              value={sourceCao}
              onChange={handleSourceCaoChange}
              placeholder="Select branch"
              emptyMessage="No branches found"
              error={sourceCaoInvalid}
              testId="coc-source-cao-select"
            />
            {sourceCaoInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>CAO is required</div>}
          </div>

          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              <TreeStructure size={13} /> Group Code <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`source-group-${formKey}`}
              options={sourceGroupList}
              getLabel={(o) => o.group_Code}
              getValue={(o) => o.group_Code}
              value={sourceGroup}
              onChange={handleSourceGroupChange}
              placeholder="Select group code"
              emptyMessage="Select a CAO first"
              disabled={!sourceBranchCode}
              error={sourceGroupInvalid}
              testId="coc-source-group-select"
            />
            {sourceGroupInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Group Code is required</div>}
          </div>

          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              <Ticket size={13} /> Ticket No <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`source-ticket-${formKey}`}
              options={sourceTicketList}
              getLabel={(o) => String(o.ticketno)}
              getValue={(o) => o.ticketno}
              value={sourceTicket}
              onChange={handleSourceTicketChange}
              placeholder="Select ticket"
              emptyMessage="Select a group code first"
              disabled={!sourceGroupCode}
              error={sourceTicketInvalid}
              testId="coc-source-ticket-select"
            />
            {sourceTicketInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Ticket No is required</div>}
          </div>
        </div>

        {sourceTicket && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginTop: '16px' }}>
            <div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
                Subscriber Name
                <span style={{ fontSize: '9px', fontWeight: '700', color: '#6B7280', background: '#F3F4F6', padding: '2px 6px', borderRadius: '4px' }}>Read-only</span>
              </label>
              <input className="input-field" style={{ fontSize: '13px', background: '#F9FAFB' }} value={sourceSubscriberName || '—'} readOnly tabIndex={-1} data-testid="coc-source-subscriber-name" />
            </div>
          </div>
        )}

        {sourceTicket && (
          <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
            {ACTION_BOXES.map(({ key, icon: Icon, label }) => (
              <button
                key={key}
                type="button"
                onClick={() => selectAction(key)}
                style={{
                  flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px',
                  padding: '14px 10px', borderRadius: '8px', cursor: 'pointer',
                  border: selectedAction === key ? '2px solid #002FA7' : '1px solid #E5E7EB',
                  background: selectedAction === key ? '#EFF4FF' : 'white',
                }}
                data-testid={`coc-action-${key}`}
              >
                <Icon size={20} color={selectedAction === key ? '#002FA7' : '#6B7280'} />
                <span style={{ fontSize: '12px', fontWeight: '600', color: selectedAction === key ? '#002FA7' : '#374151', textAlign: 'center' }}>{label}</span>
              </button>
            ))}
          </div>
        )}

        {selectedAction && (
          <>
            {showOldRecordsDivider && <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />}

            {/* SECTION B: Old Records */}
            {chitRecords.length > 0 && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                  <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>B</div>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Old Records</div>
                    <div style={{ fontSize: '11px', color: '#9CA3AF' }}>The following record(s) will be adjusted</div>
                  </div>
                </div>

                <div style={{ overflowX: 'auto', border: '1px solid #E5E7EB', borderRadius: '8px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                    <thead>
                      <tr style={{ background: '#F9FAFB' }}>
                        <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: '11px', fontWeight: '600', color: '#6B7280' }}>Subscriber Name</th>
                        <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: '11px', fontWeight: '600', color: '#6B7280' }}>Bank Branch</th>
                        {selectedAction === 'SELF_TO_ADJUSTMENT' ? (
                          <>
                            <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: '11px', fontWeight: '600', color: '#6B7280' }}>Account No.</th>
                            <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: '11px', fontWeight: '600', color: '#6B7280' }}>IFSC Code</th>
                          </>
                        ) : (
                          <>
                            <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: '11px', fontWeight: '600', color: '#6B7280' }}>Group Code</th>
                            <th style={{ textAlign: 'left', padding: '8px 12px', fontSize: '11px', fontWeight: '600', color: '#6B7280' }}>Ticket No.</th>
                          </>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {chitRecords.map((row, idx) => (
                        <tr key={row.contact_id ?? idx} style={{ borderTop: '1px solid #E5E7EB' }}>
                          <td style={{ padding: '8px 12px' }}>{row.subscriber_name}</td>
                          <td style={{ padding: '8px 12px' }}>{row.bank_branch_name}</td>
                          {selectedAction === 'SELF_TO_ADJUSTMENT' ? (
                            <>
                              <td style={{ padding: '8px 12px', fontFamily: 'monospace' }}>{row.bank_account_number}</td>
                              <td style={{ padding: '8px 12px', fontFamily: 'monospace' }}>{row.bank_ifsc_code}</td>
                            </>
                          ) : (
                            <>
                              <td style={{ padding: '8px 12px', fontFamily: 'monospace' }}>{sourceGroupCode}</td>
                              <td style={{ padding: '8px 12px', fontFamily: 'monospace' }}>{sourceTicketNo}</td>
                            </>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {sourceTicket && chitRecords.length === 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', padding: '24px', color: '#9CA3AF' }}>
                <Tray size={28} />
                <div style={{ fontSize: '13px' }}>No chit records found for the selected ticket.</div>
              </div>
            )}

            {/* SECTION C: Adjust To */}
            {chitRecords.length > 0 && needsTarget && (
              <>
                <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                  <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>C</div>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Adjust To</div>
                    <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Choose the branch, group code and ticket to move the selected record(s) to</div>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
                  <div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
                      <Buildings size={13} /> CAO <span style={{ color: '#DC2626' }}>*</span>
                    </label>
                    <SearchableSelect
                      key={`target-cao-${formKey}-${selectedAction}`}
                      options={targetCaoList}
                      getLabel={(o) => o.branch_name}
                      getValue={(o) => o.branch_name}
                      value={targetCao}
                      onChange={handleTargetCaoChange}
                      placeholder="Select branch"
                      emptyMessage="No branches found"
                      error={targetCaoInvalid}
                      testId="coc-target-cao-select"
                    />
                    {targetCaoInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>CAO is required</div>}
                  </div>

                  <div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
                      <TreeStructure size={13} /> Group Code <span style={{ color: '#DC2626' }}>*</span>
                    </label>
                    <SearchableSelect
                      key={`target-group-${formKey}-${selectedAction}`}
                      options={targetGroupList}
                      getLabel={(o) => o.group_Code}
                      getValue={(o) => o.group_Code}
                      value={targetGroup}
                      onChange={handleTargetGroupChange}
                      placeholder="Select group code"
                      emptyMessage="Select a CAO first"
                      disabled={!targetBranchCode}
                      error={targetGroupInvalid}
                      testId="coc-target-group-select"
                    />
                    {targetGroupInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Group Code is required</div>}
                  </div>

                  <div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
                      <Ticket size={13} /> Ticket No <span style={{ color: '#DC2626' }}>*</span>
                    </label>
                    <SearchableSelect
                      key={`target-ticket-${formKey}-${selectedAction}`}
                      options={targetTicketList}
                      getLabel={(o) => String(o.ticketno)}
                      getValue={(o) => o.ticketno}
                      value={targetTicket}
                      onChange={handleTargetTicketChange}
                      placeholder="Select ticket"
                      emptyMessage="Select a group code first"
                      disabled={!targetGroupCode}
                      error={targetTicketInvalid}
                      testId="coc-target-ticket-select"
                    />
                    {targetTicketInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Ticket No is required</div>}
                  </div>
                </div>
              </>
            )}

            {/* Reason */}
            {chitRecords.length > 0 && (
              <div style={{ marginTop: '20px' }}>
                <label style={{ display: 'block', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
                  Reason <span style={{ color: '#DC2626' }}>*</span>
                </label>
                <textarea
                  className="input-field"
                  style={{ fontSize: '13px', borderColor: reasonInvalid ? '#DC2626' : undefined }}
                  rows={3}
                  maxLength={500}
                  placeholder="Enter the reason for this adjustment"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  onBlur={() => setReasonTouched(true)}
                  data-testid="coc-reason"
                />
                <div style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '2px' }}>{reason.length}/500</div>
                {reasonInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Reason is required</div>}
              </div>
            )}
          </>
        )}

        {/* ACTION BAR */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#9CA3AF' }}>
            <Info size={13} /> Fields marked with <strong>*</strong> are required
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button type="button" onClick={handleClear} className="btn-secondary" disabled={saving} data-testid="coc-clear-button">
              <ArrowClockwise size={14} style={{ marginRight: '4px' }} /> Clear
            </button>
            <button type="button" onClick={confirmAdjust} className="btn-primary" disabled={saving} data-testid="coc-save-button">
              <Check size={14} style={{ marginRight: '4px' }} /> {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>

      {/* Confirm modal */}
      {showConfirmModal && (
        <div
          onClick={cancelConfirm}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 50,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: 'white', borderRadius: '10px', width: '420px', maxWidth: '90vw', overflow: 'hidden' }}
            data-testid="coc-confirm-modal"
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #E5E7EB' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <ArrowsLeftRight size={18} color="#002FA7" />
                <h5 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#111827' }}>Confirm Adjustment</h5>
              </div>
              <button type="button" onClick={cancelConfirm} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF' }}>
                <X size={16} />
              </button>
            </div>

            <div style={{ padding: '20px' }}>
              <p style={{ fontSize: '13px', color: '#6B7280', marginTop: 0 }}>
                You're about to adjust {pendingRecordCount} record(s) for this ticket using <strong>{pendingActionLabel}</strong>. Please review before confirming.
              </p>

              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>Source</p>
                  <p style={{ fontSize: '13px', color: '#111827', margin: 0 }}>{pendingSourceSummary}</p>
                </div>
                {pendingTargetSummary && (
                  <>
                    <ArrowRight size={14} color="#9CA3AF" />
                    <div style={{ flex: 1 }}>
                      <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>Target</p>
                      <p style={{ fontSize: '13px', color: '#059669', fontWeight: '600', margin: 0 }}>{pendingTargetSummary}</p>
                    </div>
                  </>
                )}
              </div>

              <div style={{ display: 'flex', gap: '8px', fontSize: '12px', color: '#6B7280', background: '#F9FAFB', padding: '10px', borderRadius: '6px' }}>
                <Info size={14} style={{ flexShrink: 0, marginTop: '2px' }} />
                <span>This action will be recorded with the reason: &quot;{pendingReason}&quot;</span>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '14px 20px', borderTop: '1px solid #E5E7EB' }}>
              <button type="button" onClick={cancelConfirm} disabled={saving} className="btn-secondary" data-testid="coc-confirm-cancel">
                Cancel
              </button>
              <button type="button" onClick={doSave} disabled={saving} className="btn-primary" data-testid="coc-confirm-save">
                <Check size={14} style={{ marginRight: '4px' }} /> Yes, Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CentralOfficeChitsForm;